import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { 
  LayoutDashboard, 
  Dumbbell, 
  Apple, 
  Moon, 
  Calendar,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Trophy,
  Award,
  GraduationCap,
  Share2,
  Target,
  ShoppingBag,
  Zap,
  User,
  TrendingUp,
  DollarSign,
  ArrowRightLeft,
  Flame,
  MoreHorizontal,
  HelpCircle,
  Bug,
  ClipboardList,
  Medal
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const DashboardLayout = ({ children, title, showBack = false }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = React.useState(false);

  // Main nav items (shown in bottom nav on mobile)
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'System' },
    { path: '/calendar-hub', icon: Calendar, label: 'Calendar' },
    { path: '/workouts', icon: Dumbbell, label: 'Training' },
    { path: '/nutrition-pro', icon: Apple, label: 'Nutrition' },
    { path: '/recovery', icon: Moon, label: 'Recovery' },
  ];

  // Secondary nav items (Tools section)
  const secondaryNavItems = [
    { path: '/game-day', icon: Zap, label: 'Game Day' },
    { path: '/goals', icon: Target, label: 'Goals & Targets' },
    { path: '/external-workout', icon: ClipboardList, label: 'Log External Training' },
    { path: '/matrix-leaderboard', icon: Medal, label: 'Matrix Leaderboard' },
    { path: '/academics', icon: GraduationCap, label: 'Academics' },
    { path: '/equipment', icon: ShoppingBag, label: 'Equipment' },
    { path: '/my-profile', icon: User, label: 'My Profile' },
    { path: '/weekly-summary', icon: TrendingUp, label: 'Performance' },
    { path: '/leaderboard', icon: Trophy, label: 'Leaderboard' },
    { path: '/weekly-challenges', icon: Flame, label: 'Weekly Challenges' },
    { path: '/nil-dashboard', icon: DollarSign, label: 'NIL Dashboard' },
    { path: '/transfer-portal', icon: ArrowRightLeft, label: 'Transfer Portal' },
    { path: '/training-program', icon: Target, label: '12-Week Program' },
    { path: '/report-issue', icon: Bug, label: 'Report Issue' },
    { path: '/help', icon: HelpCircle, label: 'Help Center' },
    { path: '/share', icon: Share2, label: 'Share Aura' },
    { path: '/badges', icon: Award, label: 'Aura Score' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;
  const isSecondaryActive = secondaryNavItems.some(item => isActive(item.path));

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-aura-bg flex flex-col">
      {/* Top Header - Mobile & Desktop */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5 safe-area-top">
        <div className="flex items-center justify-between h-14 px-4">
          {/* Left: Back button or Logo */}
          <div className="flex items-center gap-3">
            {showBack ? (
              <button 
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 text-aura-text-muted hover:text-white transition-colors"
                aria-label="Go back"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            ) : (
              <Link to="/dashboard" className="flex items-center gap-2">
                <img 
                  src="/aura-icon.png" 
                  alt="Aura" 
                  className="w-8 h-8 object-contain"
                />
                <span className="font-heading font-bold text-lg text-white tracking-tight hidden sm:block">AURA</span>
              </Link>
            )}
            {title && (
              <h1 className="font-heading font-bold text-lg text-white uppercase tracking-tight">
                {title}
              </h1>
            )}
          </div>

          {/* Right: User menu */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-aura-primary flex items-center justify-center">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <span className="font-heading font-bold text-white text-sm">
                        {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                      </span>
                    )}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-aura-surface border-white/10">
                <div className="px-3 py-2">
                  <p className="font-medium text-white">{user?.name}</p>
                  <p className="text-sm text-aura-text-muted">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2 text-white cursor-pointer">
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-aura-destructive cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pb-20 lg:pb-6">
        {children}
      </main>

      {/* Bottom Navigation - Mobile Only */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-aura-surface/95 backdrop-blur-lg border-t border-white/5 lg:hidden safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full min-w-[56px] transition-colors ${
                isActive(item.path) 
                  ? 'text-aura-primary' 
                  : 'text-aura-text-muted hover:text-white'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5 mb-1" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          ))}
          {/* More Button */}
          <button
            onClick={() => setMoreMenuOpen(true)}
            className={`flex flex-col items-center justify-center flex-1 h-full min-w-[56px] transition-colors ${
              isSecondaryActive 
                ? 'text-aura-primary' 
                : 'text-aura-text-muted hover:text-white'
            }`}
            data-testid="nav-more"
          >
            <MoreHorizontal className="w-5 h-5 mb-1" />
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* More Menu Overlay - Mobile Only */}
      {moreMenuOpen && (
        <div 
          className="fixed inset-0 z-50 lg:hidden"
          onClick={() => setMoreMenuOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Slide-up Menu */}
          <div 
            className="absolute bottom-0 left-0 right-0 bg-aura-surface rounded-t-3xl max-h-[70vh] overflow-hidden animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center py-3">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-6 pb-4 border-b border-white/5">
              <h3 className="text-lg font-bold text-white">Tools & Features</h3>
              <button 
                onClick={() => setMoreMenuOpen(false)}
                className="p-2 hover:bg-white/5 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
            
            {/* Menu Items */}
            <div className="overflow-y-auto max-h-[calc(70vh-80px)] pb-safe">
              <div className="grid grid-cols-3 gap-2 p-4">
                {secondaryNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMoreMenuOpen(false)}
                    className={`flex flex-col items-center justify-center p-4 rounded-xl transition-colors ${
                      isActive(item.path)
                        ? 'bg-aura-primary/20 text-aura-primary'
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    <item.icon className="w-6 h-6 mb-2" />
                    <span className="text-xs font-medium text-center">{item.label}</span>
                  </Link>
                ))}
              </div>
              
              {/* Logout Button */}
              <div className="px-4 pb-6">
                <button
                  onClick={() => {
                    setMoreMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center justify-center gap-2 p-4 bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar - Hidden on Mobile */}
      <aside className="hidden lg:flex fixed left-0 top-14 bottom-0 w-64 bg-aura-surface border-r border-white/5 flex-col">
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-aura-primary/10 text-aura-primary'
                  : 'text-aura-text-muted hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
          
          {/* Secondary nav items */}
          <div className="pt-4 mt-4 border-t border-white/5">
            <p className="px-4 py-2 text-xs text-aura-text-subtle uppercase tracking-wider">Tools</p>
            {secondaryNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-aura-primary/10 text-aura-primary'
                    : 'text-aura-text-muted hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
        
        <div className="p-4 border-t border-white/5">
          <Link
            to="/settings"
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive('/settings')
                ? 'bg-white/10 text-white'
                : 'text-aura-text-muted hover:text-white hover:bg-white/5'
            }`}
          >
            <Settings className="w-5 h-5" />
            <span className="font-medium">Settings</span>
          </Link>
        </div>
      </aside>

      {/* Content offset for desktop sidebar */}
      <style>{`
        @media (min-width: 1024px) {
          main {
            margin-left: 256px;
          }
        }
        
        .safe-area-top {
          padding-top: env(safe-area-inset-top);
        }
        
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom);
        }
      `}</style>
    </div>
  );
};

export default DashboardLayout;
